import { TAF_FEE_BASE_VALUE, TAF_FEE_MIN_VALUE, TradeSide, QuantityType } from './constants';
import {
  AssetPriceType,
  SecurityAssetSecurityType,
  type FixedIncomeMarketData,
} from '@fintronners/react-api/src';
import i18n from '@fintronners/react-language/src/Lang';
import FinTronConfig from '@fintronners/react-config/src/Config/FinTronConfig';
import {
  thousandFormatNumbroCurrency,
  NumberNoNaN,
} from '@fintronners/react-utils/src/numberUtilsTSX';

const EQUITY_SECURITY_TYPES: ReadonlySet<SecurityAssetSecurityType> = new Set([
  SecurityAssetSecurityType.Cs,
  SecurityAssetSecurityType.Etf,
]);

export const isEquity = (securityType: SecurityAssetSecurityType) =>
  EQUITY_SECURITY_TYPES.has(securityType);

export const getMinimumTradeAmount = (side: TradeSide, quantityType?: QuantityType) => {
  const { buyMin, sellMin } = FinTronConfig.selfDirected.trades;

  if (quantityType === QuantityType.SHARES) {
    return 1;
  }

  if (side === TradeSide.BUY) {
    return buyMin;
  } else {
    return sellMin;
  }
};

export function calculateTradeActivityFee({
  isEligible,
  tradeSide,
  sharesAmount,
  shouldApplyFee,
}: {
  isEligible: boolean;
  tradeSide: TradeSide;
  sharesAmount: number;
  shouldApplyFee: boolean;
}) {
  const isSellOrder = tradeSide === TradeSide.SELL;
  const shouldApplyTAF = isEligible && isSellOrder && shouldApplyFee;

  if (!shouldApplyTAF) {
    return 0;
  }

  const calculatedFee = TAF_FEE_BASE_VALUE * sharesAmount;

  return Math.max(calculatedFee, TAF_FEE_MIN_VALUE);
}

export function calculateTotalFees({
  baseFeeAmount,
  tradeActivityFee,
}: {
  baseFeeAmount: number;
  tradeActivityFee: number;
}) {
  return baseFeeAmount + tradeActivityFee;
}

export function calculateNetOrderAmount({
  grossOrderAmount,
  totalFees,
  tradeSide,
}: {
  grossOrderAmount: number;
  totalFees: number;
  tradeSide: TradeSide;
}) {
  if (grossOrderAmount <= 0) {
    return 0;
  }

  const isBuy = tradeSide === TradeSide.BUY;

  return isBuy ? grossOrderAmount + totalFees : grossOrderAmount - totalFees;
}

export const getMutualFundMinimumTradeAmount = (
  side: TradeSide,
  ownSecurity: boolean,
  minInitialInvestment: number,
  minSubsequentInvestment: number,
) => {
  const { buyMin, sellMin } = FinTronConfig.selfDirected.trades;

  if (side === TradeSide.BUY) {
    return ownSecurity
      ? Number(minSubsequentInvestment) || buyMin
      : Number(minInitialInvestment) || buyMin;
  }

  return sellMin;
};

export const getTradeAmountErrorMessage = (
  side: TradeSide,
  grossOrderAmount: number,
  netOrderAmount: number,
  sharesAmount?: number,
  tradableInCash?: number,
  minimumTradeAmount?: number,
  isMutualFund?: boolean,
  isMutualFundClosed?: boolean,
  ownsMutualFundSecurity?: boolean | null,
): string | undefined => {
  if (!grossOrderAmount) return undefined;
  const { maxNotionalValue, maxOrderQuantity, buyMin, sellMin } = FinTronConfig.selfDirected.trades;

  const isSellingAll = side === TradeSide.SELL && grossOrderAmount === tradableInCash;
  const fallbackMin = side === TradeSide.BUY ? buyMin : sellMin;
  const minAmount = minimumTradeAmount ?? fallbackMin;

  const validations = [
    {
      condition:
        side === TradeSide.BUY && isMutualFund && isMutualFundClosed && !ownsMutualFundSecurity,
      message: i18n.t('errors.tradeErrors.mutualFund.closedForNewInvestors'),
    },
    {
      condition: side === TradeSide.SELL && netOrderAmount <= 0,
      message: i18n.t('errors.tradeErrors.netAmount.underMin', {
        minimumTradeAmount: thousandFormatNumbroCurrency(0, { mantissa: 0 }),
      }),
    },
    {
      condition: !isSellingAll && grossOrderAmount < minAmount,
      message: i18n.t('errors.tradeErrors.amount.underMin', {
        minimumTradeAmount: thousandFormatNumbroCurrency(minAmount, { mantissa: 0 }),
      }),
    },
    {
      condition: grossOrderAmount > maxNotionalValue,
      message: i18n.t('errors.tradeErrors.amount.maxNotionalValue', {
        maxNotionalValue: thousandFormatNumbroCurrency(maxNotionalValue, { mantissa: 0 }),
      }),
    },
  ];

  if (tradableInCash) {
    validations.push({
      condition:
        side === TradeSide.BUY
          ? netOrderAmount > tradableInCash
          : grossOrderAmount > tradableInCash,
      message: i18n.t(
        `errors.tradeErrors.amount.overMax${side === TradeSide.BUY ? 'Cash' : 'Holdings'}`,
      ),
    });
  }

  /**
   * NOTE:
   * Mutual Funds do not have a maximum share quantity limit.
   * This validation is intentionally skipped for both BUY and SELL mutual fund orders
   * to allow trades resulting in more than 1,999 shares, per business requirements.
   */
  if (sharesAmount && !isMutualFund) {
    validations.push({
      condition: sharesAmount > maxOrderQuantity,
      message: i18n.t('errors.tradeErrors.amount.maxOrderQuantity', {
        maxOrderQuantity: maxOrderQuantity,
        estimatedShares: sharesAmount.toFixed(0),
      }),
    });
  }

  return validations.find((v) => v.condition)?.message;
};

export const getFixedIncomeTradeAmountErrorMessage = (
  grossOrderAmount: number,
  netOrderAmount: number,
  quantity: number,
  tradableUnits?: number,
  isSell?: boolean,
): string | undefined => {
  const { maxNotionalValue, maxOrderQuantity } = FinTronConfig.selfDirected.trades;

  const validations = [
    {
      condition: grossOrderAmount > maxNotionalValue,
      message: i18n.t('errors.tradeErrors.amount.maxNotionalValue', {
        maxNotionalValue: thousandFormatNumbroCurrency(maxNotionalValue, { mantissa: 0 }),
      }),
    },
    {
      condition: quantity > maxOrderQuantity,
      message: i18n.t('errors.tradeErrors.amount.maxOrderQuantity', {
        maxOrderQuantity: maxOrderQuantity,
        estimatedShares: quantity.toFixed(0),
      }),
    },
    {
      condition: isSell && quantity > (tradableUnits ?? 0),
      message: i18n.t('miscScreens.tradeOptions.fixedIncome.amountError'),
    },
    {
      condition: !isSell && netOrderAmount > (tradableUnits ?? 0),
      message: i18n.t('errors.tradeErrors.amount.overMaxCash'),
    },
  ];

  return validations.find((v) => v.condition)?.message;
};

export const getAskBidOverview = (
  fixedIncomeMarket: FixedIncomeMarketData | undefined | null,
  rawParValue: number | string,
) => {
  const parValue = Number(rawParValue);

  const percentageOfParBidPrice = fixedIncomeMarket?.bidPrices.find(
    (bidp) => bidp.type === AssetPriceType.AssetPriceTypePercentageOfPar,
  );

  const percentageOfParAskPrice = fixedIncomeMarket?.askPrices.find(
    (askp) => askp.type === AssetPriceType.AssetPriceTypePercentageOfPar,
  );

  const bidYTM = NumberNoNaN(
    fixedIncomeMarket?.bidPrices.find(
      (bidp) => bidp.type === AssetPriceType.AssetPriceTypeYieldToMaturity,
    )?.price,
    0,
  );

  const askYTM = NumberNoNaN(
    fixedIncomeMarket?.askPrices.find(
      (askp) => askp.type === AssetPriceType.AssetPriceTypeYieldToMaturity,
    )?.price,
    0,
  );

  const bidPrice = NumberNoNaN(percentageOfParBidPrice?.price, 0);
  const askPrice = NumberNoNaN(percentageOfParAskPrice?.price, 0);

  const askQuantity = NumberNoNaN(fixedIncomeMarket?.askQuantity / parValue, 0);
  const bidQuantity = NumberNoNaN(fixedIncomeMarket?.bidQuantity / parValue, 0);
  const askMinimumQuantity = NumberNoNaN(fixedIncomeMarket?.askMinimumQuantity / parValue, 0);
  const bidMinimumQuantity = NumberNoNaN(fixedIncomeMarket?.bidMinimumQuantity / parValue, 0);

  return {
    bidYTM,
    askYTM,
    bidPrice,
    askPrice,
    bidQuantity,
    askQuantity,
    bidMinimumQuantity,
    askMinimumQuantity,
  };
};
